// src/components/Pagination.tsx - FIXED: Use schema-driven page size and limits
import React, { useCallback } from 'react';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { formatLargeNumber, formatTime } from '../utils';

interface PaginationProps {
  loading?: boolean;
}

const PaginationInfoSkeleton: React.FC = () => (
  <div className="pagination-info-skeleton" />
);

const Pagination: React.FC<PaginationProps> = ({ loading = false }) => {
  const { state, setPage } = useAppContext();

  const handlePageChange = useCallback((newPage: number) => {
    // No artificial limits - let the API handle validation
    const safePage = Math.max(1, newPage);
    setPage(safePage);
  }, [setPage]);

  const getPaginationPages = useCallback(() => {
    const current = state.searchState.page;
    const total = state.totalPages;
    const pageCountToShow = 5;

    if (total <= pageCountToShow) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    let start = Math.max(1, current - Math.floor(pageCountToShow / 2));
    let end = start + pageCountToShow - 1;

    if (end > total) {
      end = total;
      start = Math.max(1, end - pageCountToShow + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [state.searchState.page, state.totalPages]);

  if (!loading && state.contacts.length === 0) {
    return null;
  }

  const currentPage = state.searchState.page;
  const totalPages = state.totalPages;
  const pages = getPaginationPages();

  return (
    <div className="pagination-section">
      <div className="pagination-info">
        {loading ? (
          <PaginationInfoSkeleton />
        ) : (
          <div className="pagination-range-with-timing">
            <span>
              Showing {Math.min((currentPage - 1) * state.pageSize + 1, state.totalResults).toLocaleString()} to{' '}
              {Math.min(currentPage * state.pageSize, state.totalResults).toLocaleString()} of{' '}
              {formatLargeNumber(state.totalResults)} contacts
            </span>
            {state.lastSearchTime && (
              <span className="search-timing-inline">
                <Clock size={12} />
                {formatTime(state.lastSearchTime)}
              </span>
            )}
          </div>
        )}
      </div>
      
      {totalPages > 1 && (
        <div className="pagination-controls">
          <button 
            className="pagination-button nav" 
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage <= 1}
            type="button"
          >
            <ChevronLeft size={16} />
            <span>Previous</span>
          </button>
          
          {pages.map((page) => (
            <button
              key={page}
              type="button"
              className={`pagination-button ${currentPage === page ? 'active' : ''}`}
              onClick={() => handlePageChange(Number(page))}
            >
              {page}
            </button>
          ))}
          
          <button 
            className="pagination-button nav" 
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
            type="button"
          >
            <span>Next</span>
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default Pagination;